import React from "react";
import styled, { css } from "styled-components";

import Graph from "src/library/components/Graph";
import {
  TotalTimeSpentResult,
  useWidgetDataApi
} from "src/core/dashboard/api/widget-data.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { GraphPayload } from "src/library/components/Graph/GraphToolTip";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { WidgetDisplayTypes } from "src/core/dashboard/api/dashboard.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import {
  getDisplayRange,
  getGraphMetrics,
  DisplayRangeType,
  getSelectedWidgetView,
  WidgetProps
} from "../utils";
import DataLoadError from "../DataLoadError";
import { groupTotalTimeSpent } from "./utils";
import { WidgetInnerHeader } from "../WidgetInnerHeader";
import { GraphDataType } from "../WidgetTotalValueSwitcher";
import { NumberWidget } from "../NumberWidget";
import { useWidgetFontSize } from "../hooks";

// Type for the flat object used to display data in graph
export interface TotalTimeSpentValue extends Omit<GraphPayload, "date"> {
  date: Date;
  /** value will either be count of tickets or a sum of totalTime */
  value: number;
}

const SUPPORT_TOTAL_TIME_SPENT_CACHE_KEY = "widget_support_total_time_spent";

/**
 * Returns support total time spent widget content
 */
export const TotalTimeSpentWidget: React.FunctionComponent<WidgetProps> = ({
  options, filters, widgetId, widgetTypeId, ignoreCache = false
}) => {
  const [ displayRange, setDisplayRange ] = React.useState<DisplayRangeType>("day");
  const [ graphDataType, setGraphDataType ] = React.useState<GraphDataType>("value");
  const [ totalTimeSpentResponse, totalTimeSpentRequest ] = useWidgetDataApi("GET_TOTAL_TIME_SPENT");
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { width } = useWidgetContainerSize(wrapperRef);
  const fontSize = useWidgetFontSize(width, options.view);

  // On load, get the data we need
  React.useEffect(() => {
    totalTimeSpentRequest({
      widgetTypeId,
      filters,
      options: null
    }, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => SUPPORT_TOTAL_TIME_SPENT_CACHE_KEY,
        ignoreCache
      }
    });
  }, [
    filters,
    ignoreCache,
    totalTimeSpentRequest,
    widgetTypeId
  ]);

  const selectedView = React.useMemo(() => {
    return getSelectedWidgetView(options.view);
  }, [ options ]);

  // Takes the raw response from API and transforms the result to the shape required for graph data.
  // During this process, we also sets the displayRange
  const transformToGraphData = React.useCallback((totalTimeSpentResult: TotalTimeSpentResult): TotalTimeSpentValue[] => {
    // Find range to determine whether to display in days, weeks or months
    const calculatedDisplayRange = getDisplayRange(totalTimeSpentResult);

    // Update state required by Graph component
    setDisplayRange(calculatedDisplayRange);

    // Group, accumulate totals and return
    return groupTotalTimeSpent(totalTimeSpentResult, calculatedDisplayRange, graphDataType);
  }, [ graphDataType ]);

  // The data used to graph view widget
  const graphDisplayData = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      if (totalTimeSpentResponse.data?.result) {
        return transformToGraphData(totalTimeSpentResponse.data?.result);
      }
    }

    return null;
  }, [
    totalTimeSpentResponse.data?.result,
    transformToGraphData,
    selectedView
  ]);

  const graphMetrics = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      return graphDisplayData ? getGraphMetrics(graphDisplayData, [ "date" ]) : [];
    }

    return null;
  }, [ graphDisplayData, selectedView ]);

  // The data used for number view widget
  const numberDisplayData = React.useMemo(() => {
    if (selectedView === WidgetDisplayTypes.Number) {
      const totalTimeSpentResult = totalTimeSpentResponse.data?.result;

      if (totalTimeSpentResult) {
        return { value: totalTimeSpentResult.reduce((total, totalTimeSpent) => total + parseInt(totalTimeSpent.totalTime, 10), 0) };
      }
    }

    return null;
  }, [ totalTimeSpentResponse.data?.result, selectedView ]);

  if (selectedView !== "bar" && selectedView !== "line" && selectedView !== WidgetDisplayTypes.Number) {
    // Currently, graph bar and line and number are the only handled view options availble below
    return null;
  }

  return (
    <StyledWrapper ref={wrapperRef}>
      {totalTimeSpentResponse.pending && (
        <LoadingSpinner type="circular" />
      )}

      {graphDisplayData && graphMetrics && !totalTimeSpentResponse.error && (selectedView === "line" || selectedView === "bar") && (
        <>
          <WidgetInnerHeader
            title="Total Time Spent"
            containerWidth={width}
            updateGraphDataType={setGraphDataType}
          />

          <Graph
            stacked
            containerWidth={width && width}
            data={graphDisplayData}
            type={selectedView}
            toolTipValueType={graphDataType === "value" ? "time" : "number"}
            graphMetrics={graphMetrics}
            xAxis={{
              dataKey: "date",
              dateFormat: displayRange === "month" ? "month" : "day"
            }}
          />
        </>
      )}

      {numberDisplayData && fontSize && !totalTimeSpentResponse.error && (
        <NumberWidget
          widgetId={widgetId}
          displayData={numberDisplayData}
          units="Total Time Spent"
          valueType="time"
          fontSize={fontSize}
        />
      )}

      {totalTimeSpentResponse.error && (
        <DataLoadError errorMessage={totalTimeSpentResponse.error.message} />
      )}

    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    width: 100%;
    padding: ${theme.spacing.padding.sm} 0; 
  `}
`;
